import { Component, Inject, ViewEncapsulation } from '@angular/core';
import {
  DataStateChangeEvent,
  GridDataResult,
} from '@progress/kendo-angular-grid';
import {
  ColumnOptions,
  DateHelper,
  GridRowType,
  GridState,
  GridStateCompositeFilterDescriptor,
  LoaderService,
  MessageService,
  NamedEntity,
} from '@shared';
import { ChangesetService, RestaurantService, TiersService } from '@shared-data';
import { TokenService } from '@auth';
import { GridRowAvailabilityChangeset, 
  GridRowChangeset, 
  GridRowChangesetModifierCodes, 
  GridRowRealignmentChangeset, 
  IScheduledChangeset } from '../../models';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { CHANGESET_STRATEGIES, ChangesetType } from '@restaurant-item';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrl: './history.component.scss',
  encapsulation: ViewEncapsulation.None,
   providers: [
    {
      provide: CHANGESET_STRATEGIES,
      useValue: {
        pricing: new GridRowChangeset(),
        availability: new GridRowAvailabilityChangeset(), 
        realignment: new GridRowRealignmentChangeset(),
        modifiercodes: new GridRowChangesetModifierCodes(),
      },
    },
  ],
})
export class HistoryComponent {
  changesets: GridDataResult = { data: [], total: 0 };

  pageSize = 10;
  state = new GridState(this.pageSize);
  modifierCodesEnabled: boolean = environment.modifierCodesFeatureEnabled;

  columns: ColumnOptions[] = [];
  types: NamedEntity[] = this.modifierCodesEnabled ? [
    new NamedEntity('pricing', 'Pricing'),
    new NamedEntity('availability', 'Availability'),
    new NamedEntity('realignment', 'Realignment'),
    new NamedEntity('modifiercodes', 'Modifier Codes'),
  ] : [
    new NamedEntity('pricing', 'Pricing'),
    new NamedEntity('availability', 'Availability'),
    new NamedEntity('realignment', 'Realignment'),
  ];
  
  selectedType: NamedEntity = this.types[0];
  isAvailabilityOnly: boolean = false;

  fontSize: number = 0.875;

  tiers: NamedEntity[] = [];
  selectedTier: NamedEntity = new NamedEntity('-1', 'All Tiers');

  restaurants: NamedEntity[] = [];
  selectedRestaurants: NamedEntity[] = [];
  selectedToReschedule: IScheduledChangeset[] | null = null;

  eod = DateHelper.endOfBusinessDay(new Date());

  scheduledDate: Date = this.eod;

  group: GroupDescriptor[] = [{ field: 'scheduledDate', dir: 'desc' }];

  cancelAvailabilityConfirmationPopupVisible: boolean = false;

  cancelAvailabilityEnabled: boolean = environment.cancelAvailabilityEnabled;

  constructor(@Inject(CHANGESET_STRATEGIES)
    private strategies: Record<ChangesetType, GridRowType>,
    private changesetService: ChangesetService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private tierService: TiersService,
    private tokenService: TokenService,
    private restaurantService: RestaurantService,
  ) {
  }

  async ngOnInit() {
    this.isAvailabilityOnly = await this.tokenService.isAvailabilityOnly();

    // For AvailabilityOnly users, filter types to only show Availability
    if (this.isAvailabilityOnly) {
      this.types = this.types.filter((type) => type.id === 'availability');
      this.selectedType = this.types[0];
    }

    this.getTiers();

    this.getRestaurants();
  }

  openRescheduleDialog(group: IScheduledChangeset[]) {
    this.selectedToReschedule = group;
    this.scheduledDate = this.eod;
  }

  getTiers() {
    this.loaderService.show();

    this.tierService.getTiers().subscribe({
      next: (tiers) => {
        this.tiers = tiers.map((tier) => new NamedEntity(tier.id, tier.name));
        this.tiers.unshift(new NamedEntity('-1', 'All Tiers'));
        this.selectedTier = this.tiers[0];
        this.loaderService.hide();

        this.fetchChangesets(this.state);
      },
      error: (err) => {
        this.messageService.error(`Failed to get tiers: ${err.error}`);
        this.loaderService.hide();
      },
    });
  }

  getRestaurants(name?: string) {
    this.loaderService.show();

    const filterComposite = name ? 
    new GridStateCompositeFilterDescriptor().and({
        field: 'name',
        operator: 'contains',
        value: name,
    }) 
    : new GridStateCompositeFilterDescriptor();

    const state = new GridState();
    state.filter = filterComposite;

    this.restaurantService.getRestaurants(state).subscribe({
      next: (restaurants) => {
        this.restaurants = restaurants.data.map(
          (restaurant) =>
            new NamedEntity(restaurant.id, restaurant.name),
        );
        this.loaderService.hide();
      }, 
      error: (err) => {
        this.messageService.error(`Failed to get restaurants: ${err.error}`);
        this.loaderService.hide();
      }
    });
  }

  onDataStateChanged(event: DataStateChangeEvent) {
    this.fetchChangesets(event as GridState);
  }

  setDefaultState() {
    this.state = new GridState(GridState.getPageSizeByFontSize(this.fontSize));

    this.fetchChangesets(this.state);
  }

  fetchChangesets(state: GridState) {
    this.loaderService.show();
    if (state.filter !== undefined) {
      this.state.filter = state.filter;
    }
    if (state.skip !== undefined) {
      this.state.skip = state.skip;
    }
    if (state.take !== undefined) {
      this.state.take = state.take;
    }
    if (state.sort?.length) {
      this.state.sort = state.sort;
    } else {
      this.state.sort = [{ field: 'scheduledDate', dir: 'desc' }];
    }

    if (this.state.filter) {
      this.state.filter = new GridStateCompositeFilterDescriptor(
        GridState.removeFilterByFields(
          this.state.filter,
          'type',
          'priceTierId',
          'restaurantId',
        ),
      );
    }

    this.state.filter = this.state.filter?.and({
      field: 'type',
      operator: 'eq',
      value: this.selectedType.id,
    });

    if (this.selectedTier.id != '-1') {
      this.state.filter = this.state.filter?.and({
        field: 'priceTierId',
        operator: 'eq',
        value: this.selectedTier.id,
      });
    }

    if (this.selectedRestaurants.length > 0) {
      let restaurantsFilter = new GridStateCompositeFilterDescriptor();
      this.selectedRestaurants.forEach((restaurant) => {
        restaurantsFilter = restaurantsFilter.or({
          field: 'restaurantId',
          operator: 'eq',
          value: restaurant.id,
        });
      });

      this.state.filter = this.state.filter?.and(restaurantsFilter);
    }

    if (this.group) {
      this.state.group = this.group;
    }

    this.changesetService.getAllChangesets(this.state).subscribe({
      next: (changesets) => {
        this.toggleColumnVisibility();
        this.changesets = changesets;

        this.loaderService.hide();
      },
      error: () => {
        this.messageService.error('Failed to load changesets');
        this.loaderService.hide();
      },
    });
  }

  toggleColumnVisibility() {
    if (this.selectedType) {
      const strat = this.strategies?.[this.selectedType.id as ChangesetType];
      if (!strat) return;
      console.log(strat);
      this.columns = strat
        .getColumns()
        .filter((c: ColumnOptions) => c.visibleInQuickView);
    }
  }

  cancelChangeset(dataItem: IScheduledChangeset) {
    this.loaderService.show();
    this.changesetService.cancelChangeset(dataItem.id).subscribe({
      next: () => {
        this.messageService.success('Changeset successfully cancelled');
        this.fetchChangesets(this.state);
        this.loaderService.hide();
      },
      error: () => {
        this.messageService.error('Failed to cancel changeset');
        this.loaderService.hide();
      },
    });
  }

  rescheduleChangeset(group: IScheduledChangeset[], scheduledDate: Date) {
    scheduledDate.setSeconds(0, 0); // otherwise the time picker seconds are random

    this.loaderService.show();
    this.changesetService
      .rescheduleChangeset(
        group.map((item: IScheduledChangeset) => item.id),
        scheduledDate,
      )
      .subscribe({
        next: () => {
          this.messageService.success('Changeset successfully rescheduled');
          this.fetchChangesets(this.state);
          this.selectedToReschedule = null;
          this.loaderService.hide();
        },
        error: () => {
          this.messageService.error('Failed to reschedule changeset');
          this.selectedToReschedule = null;
          this.loaderService.hide();
        },
      });
  }

  showEditButton(items: IScheduledChangeset[]): boolean {
    return items.every((item) => item.transmitStatus == 'Pending');
  }

  getTimezoneAbbreviation(date: Date): string {
    return DateHelper.getTimezoneAbbreviation(date);
  }

  cancelAvailability() {
    if (this.selectedRestaurants.length === 0) {
      this.messageService.error('Please select a restaurant to cancel availability changesets');
      return;
    }

    this.changesetService.cancelAvailabilityChangesets(
      this.selectedRestaurants.map(r => r.id)
    ).subscribe({
      next: () => {
        this.messageService.success('Availability changesets successfully cancelled');
        this.fetchChangesets(this.state);
        this.cancelAvailabilityConfirmationPopupVisible = false;
      },
      error: () => {
        this.messageService.error('Failed to cancel availability changesets');
        this.cancelAvailabilityConfirmationPopupVisible = false;
      },
    });
  }

}
